import { create } from "zustand";
import debounce from "lodash.debounce";
import {
  fetchProducts as apiFetchProducts,
  fetchSuppliers as apiFetchSuppliers,
  updateProductStatus as apiUpdateProductStatus,
} from "../services/productsService";
import { fetchCategoriesProm as apiFetchPromCategories } from "../services/promCategoriesService";
import { buildCategoryTree, flattenCategoryTree } from "utils";

// Глобальний стор (state manager) сторінки "Товари постачальників"
export const useProductsStore = create((set, get) => ({
  products: [],
  suppliers: [],
  categoryOptions: [],
  totalCount: 0,
  counts: { available: 0, order: 0, notAvailable: 0 },
  isLoading: true,

  filters: {
    search: "",
    supplier_id: null,
    category_id: null,
    is_on_shop: null,
    status: null,
    page: 1,
    page_size: 100,
    sort_by: "article",
    sort_asc: false,
  },

  // Debounced-функція для відкладеного оновлення даних з сервера
  debouncedRefresh: debounce(() => {
    get().fetchProducts();
  }, 20000),

  // Ініціалізує стор при першому завантаженні сторінки
  init: async () => {
    get().initFiltersFromURL();

    await Promise.all([
      get().fetchProducts(),
      get().fetchSuppliers(),
      get().fetchCategories(),
    ]);
  },

  // Зчитує параметри з URL і встановлює початковий стан фільтрів
  initFiltersFromURL: () => {
    const params = new URLSearchParams(window.location.search);
    set({
      filters: {
        ...get().filters,
        search: params.get("search") || "",
        supplier_id: params.get("supplier_id") || null,
        category_id: params.get("category_id") || null,
        is_on_shop: params.get("is_on_shop") || null,
        status: params.get("status") || null,
        page: parseInt(params.get("page") || "1", 10),
        sort_by: params.get("sort_by") || "article",
        sort_asc: params.get("sort_asc") !== "false",
      },
    });
  },

  // Оновлює URL-рядок браузера відповідно до поточного стану фільтрів
  syncFiltersToURL: () => {
    const { filters } = get();
    const params = new URLSearchParams();

    // Додаємо в URL лише непусті значення для чистоти
    if (filters.search) params.set("search", filters.search);
    if (filters.supplier_id) params.set("supplier_id", filters.supplier_id);
    if (filters.category_id) params.set("category_id", filters.category_id);
    if (filters.is_on_shop !== null) params.set("is_on_shop", filters.is_on_shop);
    if (filters.status) params.set("status", filters.status);
    if (filters.page !== 1) params.set("page", filters.page);
    if (filters.sort_by !== "article") params.set("sort_by", filters.sort_by);
    if (!filters.sort_asc) params.set("sort_asc", "false");

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  },

  // Керує логікою фільтрації таблиці
  applyFilters: (newFilters) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...newFilters,
        page: 1,
      },
    }));
    get().syncFiltersToURL();
    get().fetchProducts();
  },

  // Керує логікою сортування таблиці
  setSort: (field) => {
    const { sort_by, sort_asc } = get().filters;
    if (sort_by === field) {
      // Зміна напрямку
      set((state) => ({
        filters: { ...state.filters, sort_asc: !sort_asc },
      }));
    } else {
      set((state) => ({
        filters: { ...state.filters, sort_by: field, sort_asc: true, page: 1 },
      }));
    }
    get().syncFiltersToURL();
    get().fetchProducts();
  },

  // Керує логікою пагінації
  setPage: (page) => {
    set((state) => ({
      filters: { ...state.filters, page },
    }));
    get().syncFiltersToURL();
    get().fetchProducts();
  },

  // Керує логікою пошуку
  setSearch: (value) => {
    set((state) => ({
      filters: { ...state.filters, search: value, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchProducts();
  },

  // Керує логікою вибору наявності карток на Prom
  setIsOnShop: (value) => {
    set((state) => ({
      filters: { ...state.filters, is_on_shop: value, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchProducts();
  },

  // Асинхронно завантажує список товарів з сервера
  fetchProducts: async () => {
    get().debouncedRefresh.cancel();

    set({ isLoading: true });
    set({ counts: { available: 0, order: 0, notAvailable: 0 }, totalCount: 0 });
    try {
      const { data, totalCount, counts } = await apiFetchProducts(get().filters);
      set({ products: data, totalCount, counts });
    } catch (err) {
      // У випадку помилки скидаємо дані до порожніх значень
      console.error("Failed to fetch products:", err);
      set({
        products: [],
        totalCount: 0,
        counts: { available: 0, order: 0, notAvailable: 0 },
      });
    } finally {
      set({ isLoading: false });
    }
  },

  // Асинхронно завантажує список постачальників
  fetchSuppliers: async () => {
    try {
      const data = await apiFetchSuppliers();
      set({ suppliers: data });
    } catch (err) {
      // У випадку помилки скидаємо дані до порожніх значень
      console.error("Failed to fetch suppliers:", err);
      set({ suppliers: [] });
    }
  },

  // Асинхронно завантажує список категорій Prom
  fetchCategories: async () => {
    try {
      const data = await apiFetchPromCategories();
      const tree = buildCategoryTree(data);
      const options = flattenCategoryTree(tree);
      set({ categoryOptions: options });
    } catch (err) {
      console.error("Failed to fetch prom categories:", err);
      set({ categoryOptions: [] });
    }
  },

  // Оптимістично оновлює статус товару
  optimisticUpdateStatus: (article, newStatus) => {
    // Миттєво оновлюємо UI, не чекаючи відповіді сервера
    set((state) => ({
      products: state.products.map((product) =>
        product.article === article
          ? { ...product, update_status: newStatus }
          : product,
      ),
    }));

    // У фоновому режимі надсилаємо запит на сервер
    apiUpdateProductStatus(article, newStatus)
      .then(() => {
        // Успіх: запускаємо відкладене повне оновлення для синхронізації
        get().debouncedRefresh();
      })
      .catch((error) => {
        // Помилка: відкочуємо зміни, перезавантаживши дані з сервера
        console.error("Failed to update product status, rolling back:", error);
        get().fetchProducts();
      });
  },
}));
